"use client";

import type { ElementType, ReactNode } from "react";
import {
  Controller,
  useFormContext,
  type ControllerProps,
} from "react-hook-form";
import Autocomplete, {
  type AutocompleteProps,
} from "@mui/material/Autocomplete";
import type { ChipTypeMap } from "@mui/material/Chip";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import { FormField } from "./FormField";

export type FormAutoCompleteProps<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends ElementType = ChipTypeMap["defaultComponent"],
> = {
  name: string;
  label?: ReactNode;
  helperText?: ReactNode;
  required?: boolean;
  rules?: ControllerProps["rules"];
  slotProps?: {
    textfield?: Omit<
      TextFieldProps,
      "value" | "onChange" | "error" | "required" | "inputRef"
    >;
  };
} & Omit<
  AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
  "name" | "value" | "onChange" | "renderInput"
>;

export const FormAutoComplete = <
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends ElementType = ChipTypeMap["defaultComponent"],
>({
  name,
  label,
  helperText,
  required,
  rules,
  slotProps,
  multiple,
  ...rest
}: FormAutoCompleteProps<
  Value,
  Multiple,
  DisableClearable,
  FreeSolo,
  ChipComponent
>) => {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const hasError = !!fieldState.error;
        const fallbackValue = multiple ? [] : null;
        return (
          <FormField
            label={label}
            helperText={fieldState.error?.message ?? helperText}
            error={hasError}
            required={required}
            htmlFor={name}
          >
            <Autocomplete
              id={name}
              multiple={multiple}
              {...rest}
              value={field.value ?? fallbackValue}
              onChange={(_event, next) => field.onChange(next)}
              onBlur={field.onBlur}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...(slotProps?.textfield ?? {})}
                  inputRef={field.ref}
                  error={hasError}
                  required={required}
                />
              )}
            />
          </FormField>
        );
      }}
    />
  );
};
